import { useEffect } from 'react';
import { NormalizedCacheObject, from } from '@apollo/client';
import { ApolloClient } from '@apollo/client/core/ApolloClient';
import { HttpLink } from '@apollo/client/link/http/HttpLink';
import { InMemoryCache } from '@apollo/client/cache/inmemory/inMemoryCache';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { RouterProvider } from 'react-router-dom';
import 'typeface-montserrat';
import './App.css';
import UserContextProvider from './context/UserContext';
import { router } from './components/routing/Routes';
import { initGA } from './lib/app/analytics/ga';
import { GRAPHQL_API_V2_ENDPOINT, NODE_ENV } from './env';
import { setContext } from '@apollo/client/link/context';
import { AuthHeader, getAuthHeaderForUser, handleAuthError } from './lib/auth/auth.util';
import { onError } from '@apollo/client/link/error';

declare module '@mui/material/styles' {
  interface Palette {
    teal: Palette['primary'];
    tint: Palette['primary'];
    environmental: Palette['primary'];
    social: Palette['primary'];
    governance: Palette['primary'];
  }

  interface PaletteOptions {
    teal?: PaletteOptions['primary'];
    tint?: PaletteOptions['primary'];
    environmental?: PaletteOptions['primary'];
    social?: PaletteOptions['primary'];
    governance: PaletteOptions['primary'];
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#0E3A5A',
      light: '#F8FCFF',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#97A5AD',
      dark: '#7D8F99'
    },
    teal: {
      light: '#99f6e4',
      main: '#00AB97',
      dark: '#006A77',
      contrastText: '#FFFFFF'
    },
    tint: {
      main: '#FF730B'
    },
    environmental: {
      main: '#3F7E44'
    },
    social: {
      main: '#FF3A21'
    },
    governance: {
      main: '#00689D'
    }
  },
  typography: {
    fontFamily: ['Montserrat', 'sans-serif'].join(','),
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: {
      fontSize: '3rem'
    },
    h2: {
      fontSize: '2.7rem'
    },
    h3: {
      fontSize: '2rem'
    },
    h4: {
      fontSize: '1.8rem'
    },
    h5: {
      fontSize: '1.3rem'
    },
    h6: {
      fontSize: '1.1rem'
    },
    body1: {
      fontSize: '1rem'
    },
    caption: {
      fontSize: '0.9rem'
    }
  }
});

// Basic URL link for v2 API client.
const http_link = new HttpLink({ uri: GRAPHQL_API_V2_ENDPOINT, fetch });

// v2 API link with appended authentication header for currently logged in user.
const auth_link = setContext(async (_, { headers }) => {
  const auth_header: AuthHeader = await getAuthHeaderForUser();
  return {
    headers: {
      ...headers,
      ...auth_header
    }
  };
});

// Error handling link for 401 responses.
const error_link = onError(({ networkError }) => {
  // Handle network errors (HTTP status codes).
  if (networkError) {
    handleAuthError(networkError);
  }
});

export const apollo_client_v2 = new ApolloClient<NormalizedCacheObject>({
  link: from([error_link, auth_link, http_link]),
  cache: new InMemoryCache(),
  devtools: {
    enabled: NODE_ENV !== 'production' ? true : false
  }
});

function App() {
  useEffect(() => {
    initGA();
  }, []);
  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <UserContextProvider>
          <RouterProvider future={{ v7_startTransition: true }} router={router} />
        </UserContextProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
